import {
	Button,
	CircularProgress,
	LinearProgress,
	Typography,
	Paper,
	Stepper,
	Step,
	StepLabel,
	Box,
} from "@mui/material";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { CustomDateRangePicker } from "@/dentlab/src/components/custom-date-range-picker/custom-date-range-picker.component";
import { useEffect, useState } from "react";
import { SelectionRange } from "@progress/kendo-react-dateinputs";
import { DownloadIcon, Calendar, AlertCircle } from "lucide-react";
import { supabase } from "@/lib/supabase/supabaseClient";
import { SupabaseRpcEnum } from "@/lib/supabase/supabaseTypes";
import { Logger } from "@/lib/logger/Logger";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import JSZip from "jszip";
import { getLieferscheinRow, getRechnungRow } from "./database-backup-helpers";
import { useUserManagementStore } from "@/dentlab/src/store/UserManagement";
import { useStorage } from "@/dentlab/src/hooks/useStorage";

const steps = ["Zeitraum auswählen", "Daten exportieren"];

export const DatabaseBackupTab: React.FC = () => {
	const [activeStep, setActiveStep] = useState(0);
	const [progress, setProgress] = useState<number | null>(null);
	const organization = useCentralStore((state) => state.organization);
	const [retrievedDeliveryNoteData, setRetrievedDeliveryNoteData] = useState<
		any[]
	>([]);
	const [retrievedInvoiceData, setRetrievedInvoiceData] = useState<any[]>([]);
	const [dateRange, setDateRange] = useState<SelectionRange>({
		start: new Date(new Date().setDate(new Date().getDate() - 30)),
		end: new Date(),
	});
	const [isProcessing, setIsProcessing] = useState(false);

	const membersLookup = useUserManagementStore((state) => state.membersLookup);
	const { downloadBlob } = useStorage();

	const handleDateChange = (value: SelectionRange) => {
		setDateRange(value);
		setRetrievedDeliveryNoteData([]);
		setRetrievedInvoiceData([]);
		setProgress(null);
		setIsProcessing(false);
	};

	const toUTCDateString = (date: Date): string => {
		const utcDate = new Date(
			Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
		);
		return utcDate.toISOString();
	};

	const convertToCSV = (data: any[], filename: string) => {
		if (!data || data.length === 0) {
			showNotification({
				message: `Keine Daten für ${filename} gefunden`,
				type: "warning",
			});
			return { success: false, blob: null, filePath: null };
		}

		if (filename === "Lieferscheine") {
			data = data.map((item) => {
				return getLieferscheinRow(item, membersLookup);
			});
		} else if (filename === "Rechnungen") {
			data = data.map((item) => {
				return getRechnungRow(item);
			});
		}

		try {
			// Get headers from the first object
			const headers = Object.keys(data[0]);

			let csv = headers.join(";") + "\n";

			// Add data rows
			data.forEach((item) => {
				const row = headers
					.map((header) => {
						const value = item[header] === null ? "" : item[header];
						if (
							typeof value === "string" &&
							(value.includes(";") || value.includes('"'))
						) {
							return `"${value.replace(/"/g, '""')}"`;
						}
						return value;
					})
					.join(";");
				csv += row + "\n";
			});

			const filePath = `${filename}-${new Date().toISOString().split("T")[0]}.csv`;
			const blob = new Blob(["\ufeff" + csv], {
				type: "text/csv;charset=utf-8;",
			});

			return { success: true, blob, filePath };
		} catch (error) {
			Logger.log("CSV conversion error", error);
			showNotification({
				message: `Fehler beim Erstellen der CSV-Datei für ${filename}`,
				type: "error",
			});
			return { success: false, blob: null, filePath: null };
		}
	};

	const downloadCSVs = async () => {
		const zipData = new JSZip();
		let hasFiles = false;

		if (retrievedDeliveryNoteData.length > 0) {
			const { success, blob, filePath } = convertToCSV(
				retrievedDeliveryNoteData,
				"Lieferscheine"
			);
			if (success && blob && filePath) {
				zipData.file(filePath, blob);
				hasFiles = true;
			}
		}

		if (retrievedInvoiceData.length > 0) {
			const { success, blob, filePath } = convertToCSV(
				retrievedInvoiceData,
				"Rechnungen"
			);
			if (success && blob && filePath) {
				zipData.file(filePath, blob);
				hasFiles = true;
			}
		}

		if (!hasFiles) {
			showNotification({
				message: "Keine Daten zum Herunterladen verfügbar",
				type: "warning",
			});
			return;
		}

		// Generate and download the ZIP file
		try {
			const content = await zipData.generateAsync({ type: "blob" });
			const fileName = `dentLAB-Backup-${new Date().toISOString().split("T")[0]}.zip`;
			// picker can lead to error if it is not directly called by user interaction which can be 
			// the case if the extraction and download takes too long, so default download folder here
			await downloadBlob(content, fileName, true);

			showNotification({
				message:
					"Backup-Dateien wurden erfolgreich als ZIP heruntergeladen",
				type: "success",
			});
		} catch (error) {
			Logger.log("ZIP generation error", error);
			showNotification({
				message: "Fehler beim Erstellen der ZIP-Datei",
				type: "error",
			});
		}
	};

	const getDatabaseBackup = async () => {
		if (!dateRange.end || !dateRange.start || !organization?.id) {
			showNotification({
				message: "Bitte wähle einen gültigen Datumsbereich aus.",
				type: "warning",
			});
			return;
		}

		// Überprüfen, ob das Startdatum vor dem Enddatum liegt
		if (dateRange.start > dateRange.end) {
			showNotification({
				message: "Das Startdatum muss vor dem Enddatum liegen.",
				type: "error",
			});
			return;
		}

		setProgress(0);

		const uniqueDeliveryNoteDataMap = new Map();
		let lastDate = new Date(dateRange.start);
		let length = 1000;

		while (lastDate < dateRange.end && length == 1000) {
			const { data, error } = await supabase.rpc(
				SupabaseRpcEnum.GET_DELIVERY_NOTE_BACKUP,
				{
					p_organization_id: organization.id,
					p_start_date: toUTCDateString(lastDate),
					p_end_date: toUTCDateString(dateRange.end),
				}
			);

			if (error) {
				Logger.log("error", error);
				showNotification({
					message: "Fehler beim Abrufen der Lieferscheindaten",
					type: "error",
				});
				return;
			}

			if (!data || data.length === 0) {
				break;
			}

			length = data.length;

			for (const item of data) {
				const uniqueId =
					item.job_id + item.job_document_id + item.job_item_id;
				if (!uniqueDeliveryNoteDataMap.has(uniqueId)) {
					uniqueDeliveryNoteDataMap.set(uniqueId, item);
				}
			}

			const uniqueDeliveryNoteData = Array.from(
				uniqueDeliveryNoteDataMap.values()
			);
			setRetrievedDeliveryNoteData(uniqueDeliveryNoteData);
			// Update last date to continue from next point
			lastDate = new Date(data[data.length - 1].job_document_date);
			//add 1 day to be sure to get all data
			lastDate.setDate(lastDate.getDate() - 1);

			//set timeout to avoid rate limit
			await new Promise((resolve) => setTimeout(resolve, 500));

			// Update progress
			const elapsed = Number(lastDate) - Number(dateRange.start);
			const total = Number(dateRange.end) - Number(dateRange.start);
			const currentProgress = (elapsed / total) * 50;
			setProgress(currentProgress);
		}

		Logger.log(
			"retrievedDeliveryNoteData",
			uniqueDeliveryNoteDataMap.size,
			uniqueDeliveryNoteDataMap
		);

		const uniqueInvoiceDataMap = new Map();
		lastDate = new Date(dateRange.start);
		length = 1000;

		while (lastDate < dateRange.end && length == 1000) {
			const { data, error } = await supabase.rpc(
				SupabaseRpcEnum.GET_INVOICE_BACKUP,
				{
					p_organization_id: organization.id,
					p_start_date: toUTCDateString(lastDate),
					p_end_date: toUTCDateString(dateRange.end),
				}
			);

			if (error) {
				Logger.log("error", error);
				showNotification({
					message: "Fehler beim Abrufen der Rechnungendaten",
					type: "error",
				});
				return;
			}

			if (!data || data.length === 0) {
				break;
			}

			length = data.length;

			for (const item of data) {
				const uniqueId = `${item.invoice_id}-${item.job_document_id}`;
				if (!uniqueInvoiceDataMap.has(uniqueId)) {
					uniqueInvoiceDataMap.set(uniqueId, item);
				}
			}

			const uniqueInvoiceData = Array.from(uniqueInvoiceDataMap.values());
			setRetrievedInvoiceData(uniqueInvoiceData);

			lastDate = new Date(data[data.length - 1].job_document_date);
			//add 1 day to be sure to get all data
			lastDate.setDate(lastDate.getDate() - 1);

			//set timeout to avoid rate limit
			await new Promise((resolve) => setTimeout(resolve, 500));

			// Update progress
			const elapsed = Number(lastDate) - Number(dateRange.start);
			const total = Number(dateRange.end) - Number(dateRange.start);
			const currentProgress = (elapsed / total) * 50 + 50;
			setProgress(currentProgress);
		}

		Logger.log(
			"retrievedInvoiceData",
			uniqueInvoiceDataMap.size,
			uniqueInvoiceDataMap
		);

		setProgress(100);
	};

	const handleButtonClick = async () => {
		setIsProcessing(true);
		try {
			await getDatabaseBackup();
		} finally {
			setIsProcessing(false);
		}
	};

	useEffect(() => {
		if (
			!isProcessing &&
			(retrievedDeliveryNoteData.length > 0 ||
				retrievedInvoiceData.length > 0)
		) {
			downloadCSVs();
		}
	}, [retrievedDeliveryNoteData, retrievedInvoiceData, isProcessing]);

	useEffect(() => {
		setProgress(null);
		setRetrievedDeliveryNoteData([]);
		setRetrievedInvoiceData([]);
		setIsProcessing(false);
	}, []);

	const handleNext = () => {
		setActiveStep((prevActiveStep) => prevActiveStep + 1);
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const isDateRangeValid = () => {
		return (
			dateRange.start && dateRange.end && dateRange.start <= dateRange.end
		);
	};

	return (
		<div>
			<div className="px-24 py-12">
				<Stepper activeStep={activeStep} sx={{ marginBottom: 4 }}>
					{steps.map((label) => {
						return (
							<Step key={label}>
								<StepLabel>{label}</StepLabel>
							</Step>
						);
					})}
				</Stepper>

				<Paper
					elevation={0}
					sx={{
						padding: 3,
						backgroundColor: "#f8f9fa",
						border: "1px solid #e0e0e0",
						borderRadius: "8px",
						marginBottom: 2,
					}}
				>
					{activeStep === 0 ? (
						<div className="flex flex-col gap-2">
							<div className="flex items-center gap-1">
								<Calendar size={18} />
								<Typography variant="h6" color="primary">
									Zeitraum für den Export auswählen
								</Typography>
							</div>

							<Typography
								variant="body2"
								color="textSecondary"
								sx={{ marginBottom: 1 }}
							>
								Wähle einen Zeitraum aus, um Lieferscheine und
								Rechnungen als CSV-Datei herunterzuladen.
							</Typography>

							<div className="pb-4">
								<CustomDateRangePicker
									dateVal={dateRange}
									handleDateChange={handleDateChange}
								/>
							</div>

							{!isDateRangeValid() && (
								<div className="flex items-center gap-1 text-error-main mt-1">
									<AlertCircle size={16} />
									<Typography variant="caption" color="error">
										Bitte wähle einen gültigen Datumsbereich
										aus.
									</Typography>
								</div>
							)}
						</div>
					) : (
						<div className="flex flex-col gap-3">
							<div>
								<Typography
									variant="h6"
									color="primary"
									gutterBottom
								>
									Datenexport
								</Typography>

								<Typography variant="body2" gutterBottom>
									Zeitraum:{" "}
									{dateRange.start?.toLocaleDateString(
										"de-DE"
									)}{" "}
									-{" "}
									{dateRange.end?.toLocaleDateString("de-DE")}
								</Typography>

								<Typography
									variant="body2"
									color="textSecondary"
								>
									Klicke auf "Daten abrufen" um den Export zu
									starten. Die Daten werden als ZIP-Datei
									heruntergeladen.
								</Typography>
							</div>

							{progress !== null && (
								<div className="mt-1">
									<Typography
										variant="caption"
										color="textSecondary"
										gutterBottom
									>
										{progress < 100
											? "Daten werden abgerufen..."
											: "Daten erfolgreich abgerufen!"}
									</Typography>
									<LinearProgress
										sx={{
											height: 8,
											borderRadius: 4,
											marginTop: 1,
											marginBottom: 1,
										}}
										variant="determinate"
										value={progress}
									/>
								</div>
							)}

							{activeStep === 1 && isProcessing && (
								<div className="flex items-center gap-1">
									<AlertCircle size={16} color="#f44336" />
									<Typography variant="caption" color="error">
										Schliessen Sie diese Seite nicht, während
										der Download läuft.
									</Typography>
								</div>
							)}

							{(retrievedDeliveryNoteData.length > 0 ||
								retrievedInvoiceData.length > 0) && (
								<div className="p-2 bg-blue-100 rounded-md">
									<Typography variant="body2" color="primary">
										{`Gefundene Datensätze: ${retrievedDeliveryNoteData.length} Lieferscheine, ${retrievedInvoiceData.length} Rechnungen`}
									</Typography>
								</div>
							)}
						</div>
					)}
				</Paper>

				<div className="flex justify-between mt-4">
					{activeStep !== 0 && <Button
						onClick={handleBack}
						variant="text"
						color="inherit"
					>
						Zurück
					</Button>}

					<div>
						{activeStep === 0 ? (
							<Button
								variant="contained"
								color="primary"
								onClick={handleNext}
								disabled={!isDateRangeValid()}
							>
								Weiter
							</Button>
						) : (
							<Button
								variant="contained"
								color="primary"
								startIcon={
									isProcessing ? (
										<CircularProgress
											size={16}
											color="inherit"
										/>
									) : (
										<DownloadIcon size={16} />
									)
								}
								onClick={handleButtonClick}
								disabled={isProcessing}
							>
								{isProcessing
									? "Wird verarbeitet..."
									: "Daten abrufen und herunterladen"}
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
